import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import {
    RiStore2Line,
    RiBarcodeLine,
    RiDatabase2Line,
    RiMapPinLine,
    RiPhoneLine,
    RiFileCopyLine,
    RiCheckLine,
    RiPercentLine,
    RiIdCardLine,
    RiTruckLine,
    RiQrCodeLine,
    RiMailCheckLine,
    RiPrinterLine,
    RiBankLine,
    RiSmartphoneLine,
    RiMailLine,
} from "react-icons/ri";
import DashboardTooltip from "../../Components/DashboardTooltip";

const ProposalOrderClientCard = ({ proposal, isLoading = false }) => {
    const [copiedField, setCopiedField] = useState(null);
    const copyTimeoutRef = useRef(null);

    useEffect(() => {
        return () => {
            if (copyTimeoutRef.current) {
                clearTimeout(copyTimeoutRef.current);
            }
        };
    }, []);

    // Direct API mappings matching GET /api/v1/proposals/:id
    const clientName = proposal?.display_client_name || "N/A";
    const code = proposal?.restaurant?.code || null;
    const zohoId = proposal?.restaurant?.zoho_id || null;

    const vatNumber = proposal?.restaurant?.vat_number || null;
    const fiscalCode = proposal?.restaurant?.tax_id_code || null;

    const fullAddress = proposal?.restaurant?.formatted_full_address || null;
    const email = proposal?.restaurant?.email || null;

    // 7 Additional fields strictly matching API response (proposal.restaurant)
    const deliveryNotes = proposal?.restaurant?.delivery_notes || null;
    const sdiCode = proposal?.restaurant?.sdi_code || null;
    const emailPec = proposal?.restaurant?.email_pec || null;
    const fax = proposal?.restaurant?.fax || null;
    const telephone = proposal?.restaurant?.telephone || null;
    const iban = proposal?.restaurant?.iban || null;
    const mobile = proposal?.restaurant?.mobile_phone || null;

    // 1-Click Clipboard copy helper
    const handleCopy = async (text, fieldKey) => {
        if (!text || text === "N/A") return;
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(String(text));
            } else {
                const textArea = document.createElement("textarea");
                textArea.value = String(text);
                textArea.style.position = "fixed";
                textArea.style.opacity = "0";
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand("copy");
                document.body.removeChild(textArea);
            }
            setCopiedField(fieldKey);
            if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
            copyTimeoutRef.current = setTimeout(() => setCopiedField(null), 2000);
        } catch {
            // Silently fallback if clipboard access restricted
        }
    };

    const fiscalItems = [
        { key: "code", label: "Codice", value: code, icon: RiBarcodeLine, isMono: true },
        { key: "zoho", label: "Zoho ID", value: zohoId, icon: RiDatabase2Line, isMono: true },
        { key: "vat", label: "VAT / P.IVA", value: vatNumber, icon: RiPercentLine, isMono: true },
        { key: "fiscal", label: "Codice Fiscale", value: fiscalCode, icon: RiIdCardLine, isMono: true },
        { key: "sdi", label: "Codice SDI", value: sdiCode, icon: RiQrCodeLine, isMono: true },
        { key: "iban", label: "IBAN", value: iban, icon: RiBankLine, isMono: true },
    ];

    const contactItems = [
        { key: "address", label: "Indirizzo", value: fullAddress, icon: RiMapPinLine },
        { key: "email", label: "Email", value: email, icon: RiMailLine },
        { key: "email_pec", label: "Email PEC", value: emailPec, icon: RiMailCheckLine },
        { key: "telephone", label: "Telefono", value: telephone, icon: RiPhoneLine, isMono: true },
        { key: "mobile", label: "Cellulare", value: mobile, icon: RiSmartphoneLine, isMono: true },
        { key: "fax", label: "Fax", value: fax, icon: RiPrinterLine, isMono: true },
    ];

    // Premium Skeleton loader matching the exact summary layout
    if (isLoading) {
        return (
            <div className="w-full bg-gradient-to-r from-[#FAF3F4] via-[#FDF8F9] to-[#FAF3F4] border-[1.5px] border-[#8B2433]/20 rounded-2xl p-4 sm:p-5 mb-6 shadow-xs relative overflow-hidden animate-pulse">
                {/* Top Brand Wine Accent Bar */}
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#8B2433]/40 via-[#A32D3F]/40 to-[#8B2433]/20" />

                {/* Header: Icon + Client Name */}
                <div className="flex items-center gap-3 pb-3.5 mb-4 border-b border-[#8B2433]/15">
                    <div className="w-10 h-10 rounded-xl bg-white border border-[#8B2433]/20 shrink-0 aspect-square shadow-xs"></div>
                    <div className="space-y-1.5 flex-1">
                        <div className="h-5 bg-[#8B2433]/20 rounded-md w-48 sm:w-64"></div>
                    </div>
                </div>

                {/* Two-Column Summary Layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    
                    {/* Left Column Skeleton */}
                    <div className="bg-white/60 rounded-xl p-3 sm:p-3.5 border border-[#8B2433]/15 flex flex-col justify-between">
                        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-[#8B2433]/10">
                            <div className="w-4 h-4 rounded bg-[#8B2433]/20 shrink-0"></div>
                            <div className="h-3.5 bg-[#8B2433]/20 rounded w-36"></div>
                        </div>
                        <div className="space-y-1.5 flex-1">
                            {[...Array(6)].map((_, idx) => (
                                <div
                                    key={`skel-left-${idx}`}
                                    className="flex items-center justify-between py-1 px-2 rounded-lg bg-white/40 border border-[#8B2433]/5"
                                >
                                    <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                                        <div className="w-6 h-6 rounded-md bg-white border border-[#8B2433]/15 shrink-0 aspect-square shadow-2xs"></div>
                                        <div className="h-2.5 bg-[#8B2433]/15 rounded w-16 sm:w-20 shrink-0"></div>
                                        <div className="h-3 bg-[#8B2433]/20 rounded flex-1 max-w-[130px]"></div>
                                    </div>
                                    <div className="w-5 h-5 rounded bg-white border border-gray-200 shrink-0 shadow-2xs"></div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Column Skeleton */}
                    <div className="bg-white/60 rounded-xl p-3 sm:p-3.5 border border-[#8B2433]/15 flex flex-col justify-between">
                        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-[#8B2433]/10">
                            <div className="w-4 h-4 rounded bg-[#8B2433]/20 shrink-0"></div>
                            <div className="h-3.5 bg-[#8B2433]/20 rounded w-36"></div>
                        </div>
                        <div className="space-y-1.5 flex-1">
                            {[...Array(6)].map((_, idx) => (
                                <div
                                    key={`skel-right-${idx}`}
                                    className="flex items-center justify-between py-1 px-2 rounded-lg bg-white/40 border border-[#8B2433]/5"
                                >
                                    <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                                        <div className="w-6 h-6 rounded-md bg-white border border-[#8B2433]/15 shrink-0 aspect-square shadow-2xs"></div>
                                        <div className="h-2.5 bg-[#8B2433]/15 rounded w-16 sm:w-20 shrink-0"></div>
                                        <div className="h-3 bg-[#8B2433]/20 rounded flex-1 max-w-[150px]"></div>
                                    </div>
                                    <div className="w-5 h-5 rounded bg-white border border-gray-200 shrink-0 shadow-2xs"></div>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>

                {/* Delivery Notes Bottom Banner Skeleton */}
                <div className="mt-3.5 bg-white/70 rounded-xl p-2.5 sm:px-3.5 border border-[#8B2433]/15 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <div className="w-6 h-6 rounded-md bg-white border border-[#8B2433]/20 shrink-0 aspect-square shadow-2xs"></div>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div className="h-2.5 bg-[#8B2433]/15 rounded w-28 shrink-0"></div>
                            <div className="h-3 bg-[#8B2433]/20 rounded w-48 sm:w-64 max-w-full"></div>
                        </div>
                    </div>
                    <div className="w-5 h-5 rounded bg-white border border-gray-200 shrink-0 shadow-2xs"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full bg-gradient-to-r from-[#FAF3F4] via-[#FDF8F9] to-[#FAF3F4] border-[1.5px] border-[#8B2433]/20 rounded-2xl p-4 sm:p-5 mb-6 shadow-xs relative overflow-hidden transition-all duration-300">
            {/* Top Brand Wine Accent Bar */}
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#8B2433] via-[#A32D3F] to-[#8B2433]/30" />

            {/* Header: Client Name */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 pb-3.5 mb-4 border-b border-[#8B2433]/15">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white border border-[#8B2433]/25 flex items-center justify-center text-[#8B2433] shadow-xs shrink-0">
                        <RiStore2Line className="w-5 h-5" />
                    </div>
                    <div>
                        <span className="text-base sm:text-lg font-bold poppins-semibold text-[#1F2421] block">
                            {clientName}
                        </span>
                    </div>
                </div>
            </div>

            {/* Two-Column Summary Layout (Compact, No bulky individual cards) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                
                {/* Left Column: Dati Aziendali & Fiscali */}
                <div className="bg-white/60 rounded-xl p-3 sm:p-3.5 border border-[#8B2433]/15 flex flex-col justify-between">
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-[#8B2433]/10">
                        <RiIdCardLine className="w-4 h-4 text-[#8B2433]" />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-[#8B2433] poppins-semibold">
                            Dati Aziendali & Fiscali
                        </span>
                    </div>
                    <div className="space-y-1.5 flex-1">
                        {fiscalItems.map((item) => {
                            const IconComponent = item.icon;
                            const hasValue = Boolean(item.value && item.value !== "N/A");
                            return (
                                <div
                                    key={item.key}
                                    className="flex items-center justify-between py-1 px-2 rounded-lg hover:bg-white transition-all duration-150 group"
                                >
                                    <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                                        <div className="w-6 h-6 rounded-md bg-white border border-[#8B2433]/20 flex items-center justify-center text-[#8B2433] shrink-0 aspect-square shadow-2xs group-hover:bg-[#8B2433] group-hover:text-white group-hover:border-[#8B2433] transition-all duration-150">
                                            <IconComponent className="w-3.5 h-3.5" />
                                        </div>
                                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-24 shrink-0 truncate poppins-medium">
                                            {item.label}
                                        </span>
                                        <div className={`text-xs font-bold text-[#1F2421] group-hover:text-[#8B2433] transition-colors truncate flex-1 min-w-0 ${item.isMono ? "font-mono" : "poppins-semibold"}`}>
                                            {hasValue ? (
                                                <DashboardTooltip text={String(item.value)} maxLength={25} className="truncate whitespace-nowrap !max-w-none" />
                                            ) : (
                                                <span className="text-gray-400 font-normal">N/A</span>
                                            )}
                                        </div>
                                    </div>
                                    {hasValue && (
                                        <button
                                            type="button"
                                            onClick={() => handleCopy(item.value, item.key)}
                                            aria-label={`Copia ${item.label}`}
                                            className="p-1 rounded bg-white hover:bg-gray-50 border border-gray-200 hover:border-[#8B2433] text-gray-400 hover:text-[#8B2433] transition-all cursor-pointer shrink-0 shadow-2xs active:scale-95"
                                        >
                                            {copiedField === item.key ? (
                                                <RiCheckLine className="w-3 h-3 text-emerald-600" />
                                            ) : (
                                                <RiFileCopyLine className="w-3 h-3" />
                                            )}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Right Column: Recapiti & Contatti */}
                <div className="bg-white/60 rounded-xl p-3 sm:p-3.5 border border-[#8B2433]/15 flex flex-col justify-between">
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-[#8B2433]/10">
                        <RiPhoneLine className="w-4 h-4 text-[#8B2433]" />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-[#8B2433] poppins-semibold">
                            Recapiti & Contatti
                        </span>
                    </div>
                    <div className="space-y-1.5 flex-1">
                        {contactItems.map((item) => {
                            const IconComponent = item.icon;
                            const hasValue = Boolean(item.value && item.value !== "N/A");
                            return (
                                <div
                                    key={item.key}
                                    className="flex items-center justify-between py-1 px-2 rounded-lg hover:bg-white transition-all duration-150 group"
                                >
                                    <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                                        <div className="w-6 h-6 rounded-md bg-white border border-[#8B2433]/20 flex items-center justify-center text-[#8B2433] shrink-0 aspect-square shadow-2xs group-hover:bg-[#8B2433] group-hover:text-white group-hover:border-[#8B2433] transition-all duration-150">
                                            <IconComponent className="w-3.5 h-3.5" />
                                        </div>
                                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-24 shrink-0 truncate poppins-medium">
                                            {item.label}
                                        </span>
                                        <div className={`text-xs font-bold text-[#1F2421] group-hover:text-[#8B2433] transition-colors truncate flex-1 min-w-0 ${item.isMono ? "font-mono" : "poppins-semibold"}`}>
                                            {hasValue ? (
                                                <DashboardTooltip text={String(item.value)} maxLength={22} className="truncate whitespace-nowrap !max-w-none" />
                                            ) : (
                                                <span className="text-gray-400 font-normal">N/A</span>
                                            )}
                                        </div>
                                    </div>
                                    {hasValue && (
                                        <button
                                            type="button"
                                            onClick={() => handleCopy(item.value, item.key)}
                                            aria-label={`Copia ${item.label}`}
                                            className="p-1 rounded bg-white hover:bg-gray-50 border border-gray-200 hover:border-[#8B2433] text-gray-400 hover:text-[#8B2433] transition-all cursor-pointer shrink-0 shadow-2xs active:scale-95"
                                        >
                                            {copiedField === item.key ? (
                                                <RiCheckLine className="w-3 h-3 text-emerald-600" />
                                            ) : (
                                                <RiFileCopyLine className="w-3 h-3" />
                                            )}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

            </div>

            {/* Note di Consegna: Full-width Bottom Banner */}
            <div className="mt-3.5 bg-white/70 hover:bg-white rounded-xl p-2.5 sm:px-3.5 border border-[#8B2433]/15 flex items-center justify-between gap-3 transition-all group">
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div className="w-6 h-6 rounded-md bg-white border border-[#8B2433]/20 flex items-center justify-center text-[#8B2433] shrink-0 aspect-square shadow-2xs group-hover:bg-[#8B2433] group-hover:text-white group-hover:border-[#8B2433] transition-all duration-150">
                        <RiTruckLine className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0 flex-1 flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2">
                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider shrink-0 poppins-medium">
                            Note di Consegna:
                        </span>
                        <div className="text-xs font-bold text-[#1F2421] poppins-semibold min-w-0 flex-1 break-words leading-relaxed">
                            {deliveryNotes && deliveryNotes !== "N/A" ? (
                                deliveryNotes
                            ) : (
                                <span className="text-gray-400 font-normal">N/A</span>
                            )}
                        </div>
                    </div>
                </div>
                {deliveryNotes && deliveryNotes !== "N/A" && (
                    <button
                        type="button"
                        onClick={() => handleCopy(deliveryNotes, "delivery_notes")}
                        aria-label="Copia Note di Consegna"
                        className="p-1 rounded bg-white hover:bg-gray-50 border border-gray-200 hover:border-[#8B2433] text-gray-400 hover:text-[#8B2433] transition-all cursor-pointer shrink-0 shadow-2xs active:scale-95"
                    >
                        {copiedField === "delivery_notes" ? (
                            <RiCheckLine className="w-3 h-3 text-emerald-600" />
                        ) : (
                            <RiFileCopyLine className="w-3 h-3" />
                        )}
                    </button>
                )}
            </div>
        </div>
    );
};

ProposalOrderClientCard.propTypes = {
    proposal: PropTypes.object,
    isLoading: PropTypes.bool,
};

export default ProposalOrderClientCard;
